import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { navigate } from "gatsby"
import { connect } from "react-redux"
import API, { graphqlOperation } from "@aws-amplify/api"
import {
  Button,
  ModalGrid,
  ModalPersona,
  QuestionnaireActionBtns,
  QuestionnaireResult,
  QuestionnaireThankYou,
  Spinner,
} from "@thedgbrt/logology-ui-components"
import usePortal from "../../helpers/usePortal"
import { usePersonaDescData } from "./data/usePersonaDescData"
import { fetchProfile, setProfilePersona } from "../../state/actions/user"
import { resetProposals } from "../../state/actions/proposals"
import { resetProposalPreviews } from "../../state/actions/proposalPreviews"
import {
  getPersonaFromScore,
  getPersonaLabel,
} from "../../helpers/personalityScore"
import {
  getPersonalityScores,
  setPersona,
} from "../../state/actions/questionnaire"
import { trackSegmentEvent } from "../../helpers/segment"
import { trackAmplitude } from "../../helpers/amplitude"
import { trackCustomFB } from "../../helpers/fbPixel"

const Results = ({
  answers,
  fetchProfile,
  getPersonalityScores,
  personalityScores,
  resetProposals,
  resetProposalPreviews,
  startupName,
  setPersona,
  setProfilePersona,
  type,
}) => {
  const [chosenPersona, setChosenPersona] = useState(1)
  const [showSelector, setShowSelector] = useState(false)
  const personaText = usePersonaDescData(startupName)

  const calculateScores = () => getPersonalityScores(API, answers)

  useEffect(() => {
    if (!personalityScores || personalityScores.fetchStatus !== "ok") return
    const persona = getPersonaFromScore(personalityScores.data)
    setChosenPersona(persona)
  }, [personalityScores])

  useEffect(() => {
    calculateScores()
  }, []) // eslint-disable-line

  const handleChangePersona = id => {
    setChosenPersona(id)
    setShowSelector(false)
  }

  const handleSubmitConfirm = async () => {
    trackSegmentEvent("track", "Choose persona result", {
      persona: chosenPersona,
      personaName: getPersonaLabel(chosenPersona),
    })
    trackAmplitude("Choose persona result", {
      persona: chosenPersona,
      personaName: getPersonaLabel(chosenPersona),
    })
    trackCustomFB("Complete questionnaire")

    resetProposals()
    resetProposalPreviews()

    if (type === "private") {
      await setProfilePersona(chosenPersona, API, graphqlOperation)
      await fetchProfile(API, graphqlOperation)
      navigate("/myaccount/proposals")
    } else {
      setPersona(chosenPersona)
      navigate("/anonaccount/proposals")
    }
  }

  const renderActions = () => {
    return (
      <QuestionnaireActionBtns>
        <Button onClick={() => handleSubmitConfirm()}>
          Confirm these values
        </Button>
        <Button simple onClick={() => setShowSelector(true)}>
          view other choices
        </Button>
      </QuestionnaireActionBtns>
    )
  }

  const modalTarget = usePortal("questionnaire-modal-root")

  if (
    !personalityScores ||
    personalityScores.fetchStatus === "unknown" ||
    personalityScores.fetchStatus === "started"
  )
    return (
      <>
        {/* @forgiveme, spacing using empty p */}
        <p></p>
        <Spinner align="left" />
        <p>We are calculating your results.</p>
      </>
    )
  else if (personalityScores.fetchStatus === "error")
    return (
      <>
        <p>There was an error getting your results.</p>
        <Button small onClick={calculateScores}>
          Try again
        </Button>
      </>
    )
  else
    return (
      <>
        <QuestionnaireThankYou />
        <QuestionnaireResult
          title={personaText[chosenPersona].title}
          text={personaText[chosenPersona].text}
        />
        {renderActions()}
        {createPortal(
          <ModalGrid
            hidden={!showSelector}
            handleClose={() => setShowSelector(false)}
            type={2}
          >
            <ModalPersona
              handleCancel={() => setShowSelector(false)}
              handleSubmit={handleChangePersona}
              personaId={chosenPersona}
              personaText={personaText}
              scores={personalityScores.data}
              textSubmit="use this personality"
            />
          </ModalGrid>,
          modalTarget
        )}
      </>
    )
}

export default connect(
  ({ questionnaire, user }) => ({
    answers: questionnaire.answers,
    personalityScores: questionnaire.personalityScores,
    startupName: questionnaire.startupName,
  }),
  {
    fetchProfile,
    getPersonalityScores,
    resetProposals,
    resetProposalPreviews,
    setPersona,
    setProfilePersona,
  }
)(Results)
